import { FC, KeyboardEvent, SyntheticEvent, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Text from '@/components/text';
import FormControl from '../form-control';
import { FormProps, getMuiSize } from '../helpers';

interface Props extends Pick<FormProps, 'name' | 'label' | 'size'> {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

// Autocomplete reads this off the event to let a handler opt out of its own key handling
type MuiKeyboardEvent = KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean };

const useStyles = createUseStyles({
  autocomplete: {
    '& .MuiAutocomplete-inputRoot': {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      padding: '6px !important',
      maxWidth: 500,
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: 280,
    },
    '& .MuiAutocomplete-input': {
      flexGrow: 1,
    },
  },
  autocompleteWithSelections: {
    '& .MuiAutocomplete-inputRoot': {
      rowGap: 6,
    },
    '& .MuiAutocomplete-input': {
      flexBasis: '100%',
      width: '100% !important',
      minWidth: '100%',
    },
  },
});

const TextMulti: FC<Props> = ({
  name,
  value,
  onChange,
  label = '',
  placeholder = 'Type a value',
  size = 'small',
}) => {
  const styles = useStyles();
  const [draft, setDraft] = useState('');

  const hasLabel = !!label;
  const hasValues = !!value.length;

  const commitDraft = () => {
    const entry = draft.trim();
    setDraft('');

    if (!entry || value.some((existing) => existing.toLowerCase() === entry.toLowerCase())) {
      return;
    }

    onChange([...value, entry]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const muiEvent = event as MuiKeyboardEvent;
      muiEvent.defaultMuiPrevented = true;
      commitDraft();
    }
  };

  const handleChange = (_event: SyntheticEvent, next: (string | null)[]) => {
    onChange(next.filter((entry): entry is string => !!entry));
  };

  const handleInputChange = (_event: SyntheticEvent, inputValue: string, reason: string) => {
    setDraft(reason === 'input' ? inputValue : '');
  };

  const compClass = classNames(styles.autocomplete, {
    [styles.autocompleteWithSelections]: hasValues,
  });

  return (
    <Stack gap={1}>
      {hasLabel && (
        <Text size="x-small" color="grey">
          {label}
        </Text>
      )}

      <FormControl label="" name={name} size={size} fullWidth>
        <Autocomplete
          className={compClass}
          multiple
          freeSolo
          options={[]}
          value={value}
          inputValue={draft}
          onChange={handleChange}
          onInputChange={handleInputChange}
          // on the root rather than the input, so MUI's own blur handling is not replaced
          onBlur={commitDraft}
          size={getMuiSize(size)}
          fullWidth
          forcePopupIcon={false}
          renderInput={(params) => (
            <TextField
              {...params}
              hiddenLabel
              placeholder={hasValues ? '' : placeholder}
              variant="outlined"
              onKeyDown={handleKeyDown}
            />
          )}
        />
      </FormControl>

      <Text size="x-small" color="grey">
        Press Enter to add each value.
      </Text>
    </Stack>
  );
};

export default TextMulti;
